/**
 * useUnmarkDataPlate — takes back a "This is a data plate" tag (callable fieldService_galleryUnmarkDataPlate; ISS-612).
 *
 * Same shape as useMarkDataPlate: not optimistic, the caches change only once the server says yes, the server's own
 * refusal (the plate is already tied to a component, the label was not made by that button) is the toast.
 */
import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { galleryErrorMessage, useGalleryCache } from './useGeneratorGallery';

export interface UnmarkDataPlateResult {
  photoId: string;
  /** false = it already carried no tag: nothing was written. */
  changed: boolean;
}

const unmarkDataPlateFn = httpsCallable<{ organizationId: string; photoId: string }, UnmarkDataPlateResult>(
  functions,
  'fieldService_galleryUnmarkDataPlate'
);

type Tagged = { id: string; category?: string | null; categoryBy?: unknown; categoryAt?: unknown };
const cleared = <R extends { id: string }>(row: R, photoId: string): R =>
  row.id === photoId ? { ...row, category: null, categoryBy: null, categoryAt: null } : row;

export function useUnmarkDataPlate(generatorDocId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useGalleryCache(orgId, generatorDocId);

  return useMutation({
    mutationFn: async ({ photoId }: { photoId: string }) => (await unmarkDataPlateFn({ organizationId: orgId, photoId })).data,
    onError: (err) => {
      toast.error(galleryErrorMessage(err, 'Could not take back the data-plate tag.'));
    },
    onSuccess: (result, { photoId }) => {
      cache.queryClient.setQueryData<Tagged[]>(cache.galleryKey, (old) => old?.map((r) => cleared(r, photoId)));
      cache.patchLibraries((row) => cleared(row, photoId));
      toast.success(result.changed ? 'Data-plate tag removed' : 'Already not tagged');
    },
    onSettled: () => {
      cache.invalidateAll();
    },
  });
}
